/**
 * ScenarioSelector Widget
 *
 * Lets the user select their journey scenario (pre_immigration, already_abroad, etc.).
 * Required by the Journey Instance schema — scenario drives hero configuration.
 */

import { cn } from '@tonnie/foundation';
import { useTranslation } from '@tonnie/locale';
import { Button, Card, CardContent } from '@tonnie/ui';
import { BookOpen, CheckCircle2, Globe, MapPin, Plane } from '@tonnie/assets';
import { useCallback, useState } from 'react';
import type { WidgetProps } from '@tonnie/copilot';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

interface IScenarioOption {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  value: string;
}

interface IScenarioSelectorConfig {
  title?: string;
  description?: string;
}

// ═══════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════

/**
 * ScenarioSelectorWidget - Lets the user select their journey scenario.
 *
 * Presents four mutually-exclusive scenario options (pre_immigration, already_abroad,
 * relocation_only, integration_only). The selected scenario drives hero configuration
 * in the Journey Instance schema.
 *
 * @param config - Optional widget configuration (title, description)
 * @param onSelect - Callback fired with `{ scenario: string }` when the user confirms their selection
 * @returns React component for scenario selection
 */
export function ScenarioSelectorWidget({
  config,
  onSelect,
}: WidgetProps & { config?: IScenarioSelectorConfig; onSelect?: (value: unknown) => void }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string | null>(null);

  const scenarios: IScenarioOption[] = [
    {
      id: 'pre_immigration',
      title: t('journeyBuilder.scenarioPreImmigration'),
      description: t('journeyBuilder.scenarioPreImmigrationDesc'),
      icon: <Plane className="w-6 h-6" />,
      value: 'pre_immigration',
    },
    {
      id: 'already_abroad',
      title: t('journeyBuilder.scenarioAlreadyAbroad'),
      description: t('journeyBuilder.scenarioAlreadyAbroadDesc'),
      icon: <Globe className="w-6 h-6" />,
      value: 'already_abroad',
    },
    {
      id: 'relocation_only',
      title: t('journeyBuilder.scenarioRelocation'),
      description: t('journeyBuilder.scenarioRelocationDesc'),
      icon: <MapPin className="w-6 h-6" />,
      value: 'relocation_only',
    },
    {
      id: 'integration_only',
      title: t('journeyBuilder.scenarioIntegration'),
      description: t('journeyBuilder.scenarioIntegrationDesc'),
      icon: <BookOpen className="w-6 h-6" />,
      value: 'integration_only',
    },
  ];

  const handleSelect = useCallback((value: string) => {
    setSelected(value);
  }, []);

  const handleConfirm = useCallback(() => {
    if (selected) {
      onSelect?.({ scenario: selected });
    }
  }, [selected, onSelect]);

  return (
    <div className="w-full space-y-3 py-2" data-testid="widget-scenario_selector">
      {config?.title && <h3 className="text-sm font-semibold">{config.title}</h3>}
      {config?.description && <p className="text-xs text-muted-foreground">{config.description}</p>}
      <div className="space-y-2">
        {scenarios.map((scenario) => {
          const isSelected = selected === scenario.value;
          return (
            <Card
              key={scenario.id}
              role="radio"
              data-testid={`widget-scenario_selector-option-${scenario.value}`}
              tabIndex={0}
              aria-checked={isSelected}
              aria-label={scenario.title}
              onClick={() => handleSelect(scenario.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelect(scenario.value);
                }
              }}
              className={cn(
                'cursor-pointer border-2 transition-all hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                isSelected
                  ? 'border-primary bg-primary/5 shadow-sm'
                  : 'border-border hover:border-primary/30'
              )}
            >
              <CardContent className="p-3 flex items-center gap-3">
                {/* Radio indicator */}
                <div
                  className={cn(
                    'w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors',
                    isSelected
                      ? 'bg-primary border-primary text-primary-foreground'
                      : 'border-muted-foreground/40'
                  )}
                >
                  {isSelected && <div className="w-2 h-2 rounded-full bg-primary-foreground" />}
                </div>

                {/* Icon */}
                <div
                  className={cn(
                    'p-2 rounded-lg shrink-0',
                    isSelected
                      ? 'bg-primary/10 text-primary-accessible'
                      : 'bg-muted text-muted-foreground'
                  )}
                >
                  {scenario.icon}
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{scenario.title}</p>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {scenario.description}
                  </p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Confirm button */}
      {selected && (
        <div className="flex justify-end pt-1">
          <Button size="sm" onClick={handleConfirm} data-testid="widget-scenario_selector-confirm">
            <CheckCircle2 className="w-4 h-4 mr-1" />
            {t('common.confirm')}
          </Button>
        </div>
      )}
    </div>
  );
}
